import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Heart, Star, Users, Target, BookOpen } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        {/* Navigation */}
        <nav className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold text-foreground">HappyBook</span>
            </div>
            <Button
              data-testid="landing-login-btn"
              onClick={() => navigate("/auth")}
              className="btn-secondary"
            >
              Sign In
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-foreground leading-tight">
                Share <span className="text-primary">Positivity</span>,<br />
                Spread <span className="text-secondary">Happiness</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-lg">
                A social platform built exclusively for uplifting content. Connect with like-minded people who believe in the power of positivity.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="get-started-btn"
                  onClick={() => navigate("/auth")}
                  className="btn-primary text-lg px-10 py-6"
                >
                  Get Started Free
                </Button>
                <Button
                  variant="ghost"
                  className="text-lg px-10 py-6 text-foreground hover:bg-muted"
                >
                  Learn More
                </Button>
              </div>
              {/* Social proof */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary border-2 border-background"
                    />
                  ))}
                </div>
                <p className="text-muted-foreground">
                  <span className="font-semibold text-foreground">10,000+</span> happy members
                </p>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-secondary/10 rounded-full blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1706025000626-6ecadce42a82?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Happy people"
                className="relative z-10 rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
              More Than Just a Social Network
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Built with features that nurture your mental wellness and celebrate your growth
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="card-happy group">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Heart className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Positive Reactions</h3>
              <p className="text-muted-foreground">
                Express yourself with Heart, Star, and Hug reactions. No negativity, just love and encouragement.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="card-happy group">
              <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Goal Setting</h3>
              <p className="text-muted-foreground">
                Set personal goals, track progress, and earn trophies as you level up from Beginner to Champion.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="card-happy group">
              <div className="w-14 h-14 rounded-2xl bg-accent/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BookOpen className="w-7 h-7 text-accent-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Gratitude Journal</h3>
              <p className="text-muted-foreground">
                Daily reflections to cultivate mindfulness and appreciate the beautiful moments in life.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="card-happy group">
              <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="w-7 h-7 text-destructive" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Supportive Community</h3>
              <p className="text-muted-foreground">
                Connect with people who share success stories, love, and family moments in a safe space.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="card-happy group">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Star className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">AI Moderation</h3>
              <p className="text-muted-foreground">
                Smart content filtering ensures every post spreads positivity. No hate, no discrimination.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="card-happy group bg-gradient-to-br from-primary to-secondary text-white">
              <h3 className="text-xl font-semibold mb-3">Ready to Join?</h3>
              <p className="text-white/80 mb-6">
                Start your journey to a more positive social experience today.
              </p>
              <Button
                data-testid="feature-cta-btn"
                onClick={() => navigate("/auth")}
                className="bg-white text-foreground hover:bg-white/90 rounded-full px-6 py-2"
              >
                Sign Up Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-happy bg-gradient-to-r from-primary/5 to-secondary/5 text-center py-16">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-6">
              Start Spreading Happiness Today
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
              Join thousands of people who choose to see the bright side of life.
            </p>
            <Button
              data-testid="final-cta-btn"
              onClick={() => navigate("/auth")}
              className="btn-primary text-lg px-12 py-6"
            >
              Create Your Free Account
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-foreground">HappyBook</span>
            </div>
            <p className="text-muted-foreground text-sm">
              © 2024 HappyBook. Spreading positivity, one post at a time.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
